/**
 * useEventCachePrefetch — ambient prefetch layer for a resolved event.
 *
 * Strategic intent (per Craig's spec):
 *   When `useEventRecovery` resolves successfully, fan out a small,
 *   carefully-bounded set of HTTP fetches in the background so that
 *   subsequent intra-event navigation (Gallery → Spreading → Featured
 *   Tonight → Live Wall) opens INSTANTLY from the in-memory cache.
 *
 *   This is what makes the platform feel "ambient" / "alive in the
 *   hand" rather than "route-driven". Latency is the enemy of cinematic
 *   continuity — by the time a guest has flicked their thumb to the
 *   next tab, the data is already there.
 *
 * Strict tiering (matters on weaker phones / Safari with camera live):
 *
 *   TIER 1  — fired immediately, in parallel:
 *     • photos preview  (gallery thumbnail strip)
 *     • public-stats    (live guest counter / total uploads)
 *     • spreading       (spread score + featured_tonight winners)
 *     All three are tiny JSON, total cost ≤ ~30 KB.
 *
 *   TIER 2  — scheduled via requestIdleCallback, SKIPPED if:
 *     • the tab is not visible       (background tabs shouldn't burn CPU)
 *     • the camera is live           (signalled via `window.__eventdropCameraActive`)
 *     • the user is on a metered connection (NetworkInformation API)
 *     Items: film-preview kick-off, highlight-status warmup.
 *
 * The hook is fire-and-forget — it never blocks rendering, never
 * surfaces errors, and is fully cancelled on unmount / event-switch.
 */

import { useEffect } from 'react';
import axios from 'axios';
import { fetchOnce } from '../lib/eventCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Idle-callback polyfill — Safari and older Android Chrome don't ship
// requestIdleCallback yet, so we fall back to a 1.5s setTimeout which
// is conservative enough to stay clear of the first paint storm.
const onIdle = (cb) => {
  if (typeof window === 'undefined') return null;
  if (typeof window.requestIdleCallback === 'function') {
    return { id: window.requestIdleCallback(cb, { timeout: 4000 }), kind: 'idle' };
  }
  return { id: window.setTimeout(cb, 1500), kind: 'timeout' };
};

const cancelIdle = (handle) => {
  if (!handle || typeof window === 'undefined') return;
  if (handle.kind === 'idle' && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle.id);
  } else if (handle.kind === 'timeout') {
    window.clearTimeout(handle.id);
  }
};

const isMeteredConnection = () => {
  if (typeof navigator === 'undefined') return false;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData) return true;
  // 2g / slow-2g — be courteous and skip Tier 2.
  if (typeof c.effectiveType === 'string' && c.effectiveType.includes('2g')) return true;
  return false;
};

/**
 * useEventCachePrefetch(eventId)
 *
 * Pass the resolved event_id (NOT slug). Tier 1 fires immediately on
 * mount/eventId-change. Tier 2 is scheduled via requestIdleCallback
 * and cancelled cleanly on unmount.
 */
export const useEventCachePrefetch = (eventId) => {
  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    const idleHandle = { current: null };

    // ── TIER 1 ──────────────────────────────────────────────────────
    // Tiny JSON payloads, fired in parallel. We deliberately swallow
    // every error — prefetch is opportunistic, never user-facing.
    const tier1 = [
      // Gallery thumbnail strip — first thing a guest sees on /gallery.
      fetchOnce(`${eventId}:photos`, () =>
        axios.get(`${API}/events/${eventId}/photos`).then((r) => r.data)
      ).catch(() => {}),

      // Live contribution counter / total uploads — powers the social
      // proof pills on GuestLanding + GuestHome.
      fetchOnce(`${eventId}:public-stats`, () =>
        axios.get(`${API}/events/${eventId}/public-stats`).then((r) => r.data),
        { ttl: 30_000 } // shorter TTL — this is the "alive" counter
      ).catch(() => {}),

      // Spreading snapshot — includes featured_tonight, score, status.
      // Powers the Spreading page + Featured Tonight strip.
      fetchOnce(`${eventId}:spreading`, () =>
        axios.get(`${API}/events/${eventId}/spreading`).then((r) => r.data)
      ).catch(() => {}),
    ];
    Promise.allSettled(tier1).catch(() => { /* never throws anyway */ });

    // ── TIER 2 ──────────────────────────────────────────────────────
    // Heavier work — only after the page is settled, the tab is
    // visible, the camera isn't actively eating CPU, and we're not on
    // a metered connection. We KEEP the handle so we can cancel if
    // the component unmounts before idle fires (cheap React cleanup).
    idleHandle.current = onIdle(() => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (typeof window !== 'undefined' && window.__eventdropCameraActive === true) return;
      if (isMeteredConnection()) return;

      // Film preview pre-generation. POST kicks off async render; the
      // route is idempotent so this is safe to fire even if a preview
      // already exists. Cached for 5 minutes so we don't hammer the
      // backend queue when a guest taps between tabs.
      fetchOnce(`${eventId}:film-preview`, () =>
        axios.post(`${API}/events/${eventId}/film/preview`).then((r) => r.data),
        { ttl: 300_000 }
      ).catch(() => {});

      // Highlight status warmup — pre-loads the highlight reel state
      // so the host's Preview tab opens instant. Only relevant if the
      // event actually has highlights enabled, but we let the backend
      // 404 quietly.
      fetchOnce(`${eventId}:highlight-status`, () =>
        axios.get(`${API}/events/${eventId}/highlight/status`).then((r) => r.data),
        { ttl: 60_000 }
      ).catch(() => {});
    });

    return () => {
      cancelled = true;
      cancelIdle(idleHandle.current);
    };
  }, [eventId]);
};

/**
 * markCameraActive(true|false) — guests/booths call this around
 * MediaRecorder lifetime so Tier 2 prefetch gives up CPU/network while
 * the camera is live. Optional but recommended for the booth pages.
 */
export const markCameraActive = (active) => {
  if (typeof window === 'undefined') return;
  window.__eventdropCameraActive = !!active;
};

export default useEventCachePrefetch;
